import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "./ClientDashboardDemoComponents.module.scss";
import WidgetDemo from "./WidgetDemo";
import moment from "moment";

function BatteryStatusWidgetDemo({buildingId}) {
  const [batteryDetails, setBatteryDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastChargeDate, setLastChargeDate] = useState(null);
  const [lastDischargeDate, setLastDischargeDate] = useState(null);

  useEffect(() => {
    let varPriority = ["Non-Critical", "Critical"];

    const convertRowsToObject = (array) => {
      const objects = {};
      for (const item of array) {
        objects[item.tag] = item.value;
      }

      objects["SOC"] = ((objects["Average SOC (%)"] / 100) * 271).toFixed(2);
      objects["PriorityLevel"] = varPriority[objects["varPriority"]];
      return objects;
    };

    const averageField = (batteryObjects, field) => {
      const values = batteryObjects
        .map((battery) => Number(battery[field]))
        .filter((value) => Number.isFinite(value));

      if (!values.length) return 0;

      const total = values.reduce((sum, value) => sum + value, 0);
      return Number((total / values.length).toFixed(2));
    };

    const aggregateStatus = (batteryObjects) => {
      const statuses = batteryObjects
        .map((battery) => Number(battery["CnD"]))
        .filter((status) => Number.isFinite(status));

      if (!statuses.length) return 0;

      const hasCharging = statuses.includes(-1);
      const hasDischarging = statuses.includes(1);
      const hasIdle = statuses.some((status) => status !== -1 && status !== 1);

      if (hasCharging && hasDischarging) return "mixed";
      if (hasCharging && hasIdle) return "mixed";
      if (hasDischarging && hasIdle) return "mixed";
      if (hasCharging) return -1;
      if (hasDischarging) return 1;
      return 0;
    };

    const convertArrayToObjects = (array) => {
      const batteryRows = Array.isArray(array?.[0]) ? array : [array];
      const batteryObjects = batteryRows
        .filter((rows) => Array.isArray(rows) && rows.length > 0)
        .map(convertRowsToObject);

      if (batteryObjects.length <= 1) {
        return batteryObjects[0] || {};
      }

      return {
        "Average SOC (%)": averageField(batteryObjects, "Average SOC (%)"),
        "Average SOH (%)": averageField(batteryObjects, "Average SOH (%)"),
        "dlsScore": averageField(batteryObjects, "dlsScore"),
        "CnD": aggregateStatus(batteryObjects),
        "batteryCount": batteryObjects.length,
      };
    };

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `/batteries/getBatteryTableDataClient?buildingId=${buildingId}`
        );
        if (response.status === 200) {
          setBatteryDetails(convertArrayToObjects(response.data));
          console.log(Object.keys(batteryDetails).length);
        }
      } catch (err) {
        console.error("Error fetching battery data", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchActions = async () => {
      try {
        const response = await axios.get("/actions/getActions", {
          params: {
            page: 1,
            buildingId: buildingId,
          },
        });

        if (response.status === 200) {
          const rows = response.data.rows || [];
          const chargeAction = rows.find((item) => String(item.event).toLowerCase().includes("charge"));
          if (chargeAction) {
            setLastChargeDate(moment(chargeAction.created_at).format("YYYY-MM-DD HH:mm"));
          } else {
            setLastChargeDate(null);
          }
          const dischargeAction = rows.find((item) => String(item.event).toLowerCase().includes("discharge"));
          if (dischargeAction) {
            setLastDischargeDate(moment(dischargeAction.created_at).format("YYYY-MM-DD HH:mm"));
          } else {
            setLastDischargeDate(null);
          }
        }
      } catch (err) {
        console.error("Error fetching actions", err);
      }
    };

    if (buildingId) {
      fetchData();
      fetchActions();
    }
  }, [buildingId]);

  // Values from backend
  const SOC = Math.round(Number(batteryDetails["Average SOC (%)"]) * 10) / 10 || 0;
  const SOH = Math.round(Number(batteryDetails["Average SOH (%)"]) * 10) / 10 || 0;
  const rawDLS = Number(batteryDetails["dlsScore"]) || 0;
  const CnD = batteryDetails["CnD"] === "mixed" ? "mixed" : Number(batteryDetails["CnD"]) || 0; // -1 = Charging, 1 = Discharging, "mixed" = mixed BESS states, else Idle

  const statusText = Object.keys(batteryDetails).length === 0 ? "Offline" : CnD === -1 ? "Charging" : CnD === 1 ? "Discharging" : CnD === "mixed" ? "Mixed" : "Idle";
  const isOk = statusText === "Idle";

  // Map DLS to a 0-100 ring safely
  const DLSPercent = Math.max(0, Math.min(100, 100 + rawDLS));
  if (loading) return <div>Loading battery data...</div>;

  return (
    <WidgetDemo title="Battery Status">
      <div className={styles["battery-card"]}>
        <div className={styles["battery-card__header"]}>
          {/* <div>{selectedBattery || ""}</div> */}
          <span className={`${styles["status-pill"]} ${isOk ? styles["status-pill--ok"] : ""}`}>{statusText}</span>
        </div>

        <div className={styles["battery-card__metrics"]}>
          <div className={styles["ring"]} style={{ "--p": SOC }}>
            <div className={styles["ring__wrap"]}>
              <div className={styles["ring__circle"]}></div>
              <div className={styles["ring__value"]}>{`${SOC}%`}</div>
            </div>
            <div className={styles["ring__label"]}>SOC</div>
          </div>

          <div className={styles["ring"]} style={{ "--p": SOH }}>
            <div className={styles["ring__wrap"]}>
              <div className={styles["ring__circle"]}></div>
              <div className={styles["ring__value"]}>{`${SOH}%`}</div>
            </div>
            <div className={styles["ring__label"]}>SOH</div>
          </div>

          <div className={styles["ring"]} style={{ "--p": DLSPercent }}>
            <div className={styles["ring__wrap"]}>
              <div className={styles["ring__circle"]}></div>
              <div className={styles["ring__value"]}>{`${DLSPercent}`}</div>
            </div>
            <div className={styles["ring__label"]}>DLS</div>
          </div>
        </div>

        <div className={styles.SimpleHeaderRow}>
          <div className={styles.SimpleHeaderBlock}>
            <div>
              Last Charge: <b>{lastChargeDate || "No Charge Action"}</b>
            </div>
          </div>
        </div>
        <div className={styles.SimpleHeaderRow}>
          <div className={styles.SimpleHeaderBlock}>
            <div>
              Last Discharge: <b>{lastDischargeDate || "No Discharge Action"}</b>
            </div>
          </div>
        </div>
      </div>
    </WidgetDemo>
  );
}

export default BatteryStatusWidgetDemo;
